import axios from 'axios';

import { getAPIUrl } from './baseAPI';

// Types
export interface ResponseAICoding {
  id: number;
  questionCategoryId: number;
  respondentId: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CompletedQuestionResponse {
  response: string;
  respondentId: number;
  responseCodingCategories: {
    responseAICodingId: number; // This is the ID we use for deletion
    id: number; // This is the category ID
    name: string;
  }[];
}

// Classification endpoints
export const CLASSIFY_RESPONSES = () => ({
  path: '/responseAICodings/classify',
  version: 'v2' as const,
});

export async function classifyResponses({
  questionId,
  waveId,
}: {
  questionId: number;
  waveId?: number;
}) {
  return (
    await axios.post<void>(getAPIUrl(CLASSIFY_RESPONSES()), {
      questionId,
      waveId,
    })
  ).data;
}

export const FETCH_COMPLETED_QUESTION_RESPONSES = ({
  questionId,
  waveId,
}: {
  questionId: number;
  waveId?: number;
}) => ({
  path: waveId
    ? `/responseAICodings/completedQuestionResponses/${questionId}/wave/${waveId}`
    : `/responseAICodings/completedQuestionResponses/${questionId}`,
  version: 'v2' as const,
});

export async function fetchCompletedQuestionResponses({
  questionId,
  waveId,
}: {
  questionId: number;
  waveId?: number;
}) {
  return (
    await axios.get<CompletedQuestionResponse[]>(
      getAPIUrl(FETCH_COMPLETED_QUESTION_RESPONSES({ questionId, waveId })),
    )
  ).data;
}

// CRUD endpoints for ResponseAICoding
export const CREATE_RESPONSE_AI_CODING = () => ({
  path: '/responseAICodings',
  version: 'v2' as const,
});

export async function createResponseAICoding({
  questionCategoryId,
  respondentId,
}: {
  questionCategoryId: number;
  respondentId: number;
}) {
  return (
    await axios.post<ResponseAICoding>(getAPIUrl(CREATE_RESPONSE_AI_CODING()), {
      questionCategoryId,
      respondentId,
    })
  ).data;
}

export const FETCH_RESPONSE_AI_CODING = ({ id }: { id: number }) => ({
  path: `/responseAICodings/${id}`,
  version: 'v2' as const,
});

export async function fetchResponseAICoding({ id }: { id: number }) {
  return (
    await axios.get<ResponseAICoding>(
      getAPIUrl(FETCH_RESPONSE_AI_CODING({ id })),
    )
  ).data;
}

export const FETCH_RESPONSE_AI_CODINGS_FOR_QUESTION_CATEGORY = ({
  questionCategoryId,
}: {
  questionCategoryId: number;
}) => ({
  path: `/responseAICodings/questionCategory/${questionCategoryId}`,
  version: 'v2' as const,
});

export async function fetchResponseAICodingsForQuestionCategory({
  questionCategoryId,
}: {
  questionCategoryId: number;
}) {
  return (
    await axios.get<ResponseAICoding[]>(
      getAPIUrl(
        FETCH_RESPONSE_AI_CODINGS_FOR_QUESTION_CATEGORY({
          questionCategoryId,
        }),
      ),
    )
  ).data;
}

export const FETCH_RESPONSE_AI_CODINGS_BY_RESPONDENT = ({
  questionId,
  respondentId,
}: {
  questionId: number;
  respondentId: number;
}) => ({
  path: `/responseAICodings/respondent/${respondentId}/question/${questionId}`,
  version: 'v2' as const,
});

export async function fetchResponseAICodingsByRespondent({
  questionId,
  respondentId,
}: {
  questionId: number;
  respondentId: number;
}) {
  return (
    await axios.get<ResponseAICoding[]>(
      getAPIUrl(
        FETCH_RESPONSE_AI_CODINGS_BY_RESPONDENT({ questionId, respondentId }),
      ),
    )
  ).data;
}

export const DELETE_RESPONSE_AI_CODING = ({ id }: { id: number }) => ({
  path: `/responseAICodings/${id}`,
  version: 'v2' as const,
});

export async function deleteResponseAICoding({ id }: { id: number }) {
  return (await axios.delete(getAPIUrl(DELETE_RESPONSE_AI_CODING({ id }))))
    .data;
}
